import React, { useEffect } from "react";

import {
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverHeader,
  PopoverBody,
  PopoverArrow,
  PopoverCloseButton,
  Box,
  Badge,
  IconButton,
  Text,
  Divider,
  Portal,
} from "@chakra-ui/react";
import { IoMdNotificationsOutline } from "react-icons/io";
import Button from "components/Button";
import axiosInstance from "services/axiosInstance";
import { ENDPOINTS } from "api/endpoints";

interface Notification {
     id: number;
    title: string;
    message: string;
    data: string;
    timestamp: string;
    is_read: boolean;
}

interface NotificationData {
    unseen_count: number;
    notifications: Notification[];
}

interface NotificationButtonState extends NotificationData{
}
export default function NotificationButton() {
    
    const [state, setState] = React.useState<NotificationButtonState>({
        unseen_count: 0,
        notifications: [],
    });
    
    const fetchNotifications = async () => {
        const notifications = await axiosInstance.get<NotificationData>(ENDPOINTS.NOTFICATIONS);
        
        if (notifications.status === 200) {
            setState((prevState) => {
                return {
                    ...prevState,
                    unseen_count: notifications.data.unseen_count,
                    notifications: notifications.data.notifications,
                };
            });
        }
        
    };
  
    useEffect(() => {
        fetchNotifications();
    }, []);
    
    const onMarkAsSeen = async () => {
        if (state.unseen_count > 0) {
            const response = await axiosInstance.post(ENDPOINTS.NOTIFICATIONS_MARK_AS_SEEN);
            
            if (response.status === 200) {
                setState((prevState) => ({
                    ...prevState,
                    unseen_count: 0,
                }));
            }
        }
    };
    
    const onMarkAllAsRead = async () => {
        
        const response = await axiosInstance.post(ENDPOINTS.NOTIFICATIONS_MARK_ALL_AS_READ);
        
        if (response.status === 200) {
            setState((prevState) => {
                const updatedNotifications = prevState.notifications.map((notif) => ({
                    ...notif,
                    is_read: true,
                }));
                
                return {
                    ...prevState,
                    notifications: updatedNotifications,
                };
            });
        }
        
    };
    
    const onClickRead = (item: Notification) => async () => {
        
        if (item.data.startsWith("http")) {
            window.open(item.data, '_blank');
        }
        
        if (item.is_read) return;
        
        const endpoint = ENDPOINTS.NOTIFICATIONS_MARK_AS_READ.replace("{}", item.id.toString());
        
        const response = await axiosInstance.post(endpoint);
        
        if (response.status === 200) {
            setState((prevState) => {
                const updatedNotifications = prevState.notifications.map((notif) => {
                    if (notif.id === item.id) {
                        return {
                            ...notif,
                            is_read: true,
                        };
                    }
                    return notif;
                });
                
                return {
                    ...prevState,
                    notifications: updatedNotifications,
                };
            });
        }
    };
    
    const getCard = (item: Notification) => {
        return (
            <Box
                display="flex"
                gap="5px"
                borderBottom="1px solid #E2E8F0"
                key={item.id}
                padding="10px"
                onClick={onClickRead(item)}
                _hover={{
                    backgroundColor: 'rgba(0, 0, 0, 0.03)',
                    cursor: 'pointer',
                }}
            >
                <Box
                    position="relative"
                >
                    <Text
                        fontStyle={item.is_read ? "normal" : "italic"}
                        fontSize="sm"
                        fontWeight={!item.is_read ? 600 : 'normal'}
                        display="flex"
                        alignItems="flex-start"
                        gap="5px"
                    >
                        {item.title}
                        {!item.is_read && (
                            <Badge variant='outline' colorScheme='red'>
                                Unread
                            </Badge>
                        )}
                    </Text>
                    <Text 
                        fontSize="xs" 
                        color="gray.600"
                        fontWeight={!item.is_read ? 600 : 'normal'}
                    >
                        {item.message}
                    </Text>
                    <Text 
                        fontSize="xs" 
                        color="gray.400"
                        fontWeight={!item.is_read ? 600 : 'normal'}
                    >
                        {item.timestamp}
                    </Text>
                </Box>
            </Box>
        );
    };
        
    // Split notifications into new and earlier
    const todayNotifications = state.notifications.filter(
        (item) => /\d+[hm] ago$/.test(item.timestamp) || item.timestamp === 'just now'
    );
    const earlierNotifications = state.notifications.filter(
        (item) => !/\d+[hm] ago$/.test(item.timestamp ) && item.timestamp !== 'just now'
    );
    
    return (
        <Popover onOpen={onMarkAsSeen}>
            <PopoverTrigger>
                <Box position="relative" display="inline-block" marginRight="10px">
                    <IconButton
                        aria-label=""
                        icon={<IoMdNotificationsOutline fontSize="24px" />}
                        backgroundColor="transparent"
                    />
                    {state.unseen_count > 0 && (
                        <Badge
                            position="absolute"
                            top="0"
                            right="0"
                            transform="translate(25%, -25%)"
                            colorScheme="red"
                            borderRadius="full"
                            px={2}
                            fontSize="0.7em"
                        >
                            {state.unseen_count}
                        </Badge>
                    )}
                </Box>
            </PopoverTrigger>
            <Portal>
                <PopoverContent>
                    <PopoverArrow />
                    <PopoverCloseButton />
                    <PopoverHeader>
                        <Box
                            display="flex"
                            gap="5px"
                            justifyContent="space-between"
                            paddingRight="30px"
                        >
                            <Text fontWeight="bold">Notifications</Text>
                            <Text
                                cursor="pointer"
                                color="blue.500"
                                outline="none"
                                _hover={{ textDecoration: "underline" }}
                                onClick={onMarkAllAsRead}
                                fontSize="sm"
                            >
                                Mark all as read
                            </Text>
                        </Box>
                    </PopoverHeader>
                    <PopoverBody
                        paddingRight="0"
                    >
                        <Box
                            maxHeight="350px"
                            overflowY="auto"
                            paddingRight="12px"
                        >
                            {todayNotifications.length > 0 && (
                                <>
                                    <Text fontSize="sm" fontWeight="bold">
                                        New
                                    </Text>
                                    {/* new notification are having <h ago, m ago> */}
                                    {todayNotifications.map(getCard)}
                                    <Divider marginY="10px" />
                                </>
                            )}
                            {earlierNotifications.length > 0 && (
                                <>
                                    <Text fontSize="sm" fontWeight="bold">
                                        Earlier
                                    </Text>
                                    {/* earlier */}
                                    {earlierNotifications.map(getCard)}
                                </>
                            )}
                        </Box>
                    </PopoverBody>
                </PopoverContent>
            </Portal>
        </Popover>
    );
}
